import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router';

interface ErrorFallbackProps {
  error: Error;
  reset?: () => void;
}

/**
 * Error Fallback Component
 *
 * Displays a user-friendly error message when an error boundary catches an error.
 * Provides options to retry or navigate home.
 *
 * Features:
 * - Clear error messaging
 * - Action buttons for recovery
 * - Shows error details in development mode
 * - Responsive design
 */
export default function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
    // Reset error state if reset function is provided
    reset?.();
  };

  const handleRetry = () => {
    reset?.();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <CardTitle className="text-2xl">Something went wrong</CardTitle>
          <CardDescription>
            We encountered an unexpected error. Don't worry, your data is safe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Error Message */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-900 mb-1">Error details:</p>
            <p className="text-sm text-gray-600 font-mono break-words">{error.message}</p>
          </div>

          {/* Stack Trace in Development */}
          {import.meta.env.DEV && error.stack && (
            <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <summary className="text-sm font-medium text-gray-900 cursor-pointer">
                Stack trace (development only)
              </summary>
              <pre className="mt-2 text-xs text-gray-600 overflow-auto max-h-64 font-mono">
                {error.stack}
              </pre>
            </details>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {reset && (
              <Button onClick={handleRetry} className="flex-1" variant="default">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            )}
            <Button onClick={handleGoHome} className="flex-1" variant="outline">
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-gray-500 pt-2">
            If this problem persists, please contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
